import {
  PRODUCT_COMPANY_STATUS_DISPLAY_ORDER,
  PRODUCT_COMPANY_STATUS_TYPE_ENUM,
  getProductCompanyStatusTypeTranslated,
} from 'enums';
import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import BlockIcon from '@mui/icons-material/Block';
import type { ChipProps, Theme } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import type { ProjectCompanyCodeStatusCounts } from 'utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, Chip, Stack, Typography, useTheme } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';

type ProductCompanyStatusChipProps = {
  status: PRODUCT_COMPANY_STATUS_TYPE_ENUM;
  count?: number;
  label?: ReactNode;
  sx?: ChipProps['sx'];
};

type ProductCompanyStatusSummaryProps = {
  counts: ProjectCompanyCodeStatusCounts;
  showEmptyStatuses?: boolean;
};

export const getProductCompanyStatusVisual = (status: PRODUCT_COMPANY_STATUS_TYPE_ENUM, theme: Theme) => {
  switch (status) {
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE:
      return {
        color: theme.palette.mode === 'light' ? theme.palette.success.dark : theme.palette.success.light,
        backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
        borderColor: alpha(theme.palette.success.main, 0.4),
        icon: <CheckCircleIcon fontSize="inherit" />,
      };
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.TO_BE_INTEGRATED:
      return {
        color: theme.palette.mode === 'light' ? theme.palette.warning.dark : theme.palette.warning.light,
        backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
        borderColor: alpha(theme.palette.warning.main, 0.4),
        icon: <AutorenewIcon fontSize="inherit" />,
      };
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.NOT_IN_CONTRACT:
      return {
        color: theme.palette.mode === 'light' ? theme.palette.error.dark : theme.palette.error.light,
        backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.1),
        borderColor: alpha(theme.palette.error.main, 0.4),
        icon: <BlockIcon fontSize="inherit" />,
      };
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.PASSIVE:
    default:
      return {
        color: theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.grey[300],
        backgroundColor: alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.2 : 0.12),
        borderColor: alpha(theme.palette.grey[500], 0.4),
        icon: <PauseCircleOutlineIcon fontSize="inherit" />,
      };
  }
};

export const getProductCompanyStatusLabel = (status: PRODUCT_COMPANY_STATUS_TYPE_ENUM, intl: IntlShape): string => {
  switch (status) {
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE:
      return intl.formatMessage({ id: 'enum.productCompanyStatusType.active', defaultMessage: 'Aktif' });
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.TO_BE_INTEGRATED:
      return intl.formatMessage({
        id: 'enum.productCompanyStatusType.toBeIntegrated',
        defaultMessage: 'Entegre Edilecek',
      });
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.NOT_IN_CONTRACT:
      return intl.formatMessage({
        id: 'enum.productCompanyStatusType.notInContract',
        defaultMessage: 'Sözleşmede Yok',
      });
    case PRODUCT_COMPANY_STATUS_TYPE_ENUM.PASSIVE:
    default:
      return intl.formatMessage({ id: 'enum.productCompanyStatusType.passive', defaultMessage: 'Pasif' });
  }
};

const ProductCompanyStatusChip = ({ status, count, label, sx }: ProductCompanyStatusChipProps) => {
  const theme = useTheme();
  const visual = getProductCompanyStatusVisual(status, theme);
  const translatedStatus = getProductCompanyStatusTypeTranslated(status);
  const resolvedLabel: ReactNode =
    label ??
    (count === undefined ? (
      translatedStatus
    ) : (
      <>
        {count} {translatedStatus}
      </>
    ));

  return (
    <Chip
      icon={visual.icon}
      label={resolvedLabel}
      size="small"
      sx={[
        {
          height: 24,
          color: visual.color,
          bgcolor: visual.backgroundColor,
          border: `1px solid ${visual.borderColor}`,
          fontSize: '0.72rem',
          fontWeight: 600,
          '& .MuiChip-icon': { color: 'inherit' },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

export const ProductCompanyStatusSummary = ({
  counts,
  showEmptyStatuses = false,
}: ProductCompanyStatusSummaryProps) => {
  const intl = useIntl();
  const statuses = PRODUCT_COMPANY_STATUS_DISPLAY_ORDER.filter((status) => showEmptyStatuses || counts[status] > 0);

  if (statuses.length === 0) {
    return (
      <Typography
        component="span"
        variant="body2"
        color="text.disabled"
        aria-label={intl.formatMessage({
          id: 'project.table.noCompanyCodes',
          defaultMessage: 'Tanımlı şirket kodu yok',
        })}>
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
      {statuses.map((status) => (
        <ProductCompanyStatusChip key={status} status={status} count={counts[status]} />
      ))}
    </Stack>
  );
};

export default ProductCompanyStatusChip;
